import type { LoaderFunction } from 'react-router';

import type { RouteProps } from '../../../shared/RouteProps';
import { extractCompanyIdFromSlug } from '../../../shared/utils/extractCompanyIdFromSlug';
import { REVIEWS_PER_PAGE } from '../constants';
import { mapSalariesModel } from './mappers';

export const OPEN_JOBS_LISTING_SIZE_FOR_SALARIES = 4; // Default page size for salaries

export const salariesLoader =
  ({ services }: RouteProps): LoaderFunction =>
  async ({ params, request }) => {
    const companyId = extractCompanyIdFromSlug(params.companySlug);
    if (companyId === undefined) {
      throw new Error(
        `Could not find companyId from params: ${JSON.stringify(params)}`,
      );
    }

    const url = new URL(request.url);
    const isDraft = url.searchParams.get('draft') === 'true';

    const companyDetails =
      await services.companyDetailsService.getCompanyDetails(
        companyId,
        Boolean(isDraft),
      );

    const companySalaryRangeResult =
      await services.companyDetailsService.getCompanySalaryRange(companyId);

    const jobsData = await services.companyProfileService.getJobs(
      companyDetails?.companyProfile?.organisationId ?? undefined,
      1,
      OPEN_JOBS_LISTING_SIZE_FOR_SALARIES,
    );

    const reviews = await services.companyProfileService.getReviews({
      companyId,
      sortOrder: 'most recent',
      perPage: REVIEWS_PER_PAGE,
    });

    const formattedDateInUTC = new Date().toISOString().split('T')[0];

    const industry = companyDetails.companyProfile?.data?.overview?.industry;

    // Fetch random companies in the same industry, excluding the current company
    // Always fetch companies even if the industry is not defined, to ensure we have a fallback
    // Random countries are for SEO purposes and to provide a diverse set of companies
    const randomCompaniesInThisIndustry =
      await services.companyProfileService.getGroupedCompanies({
        groupBy: {
          group: 'INDUSTRY',
          value: industry ? [industry] : [],
        },
        perPage: 13,
        excludedCompanyIds: [companyId],
        randomSeed: formattedDateInUTC,
      });

    return mapSalariesModel(
      companyId,
      companyDetails,
      jobsData,
      randomCompaniesInThisIndustry,
      companySalaryRangeResult,
      reviews,
    );
  };
